import { useEffect, useRef, useState } from 'react';
import { Autocomplete, MenuListItem, MultiInput, INPUT_SIZES } from 'cdk-radial';
import { useTranslation } from 'react-i18next';

const MultiAutocomplete = ({ label, placeholder, options, onSelect, selectedOption } : {
   label:string,
    placeholder:any,
    options: { label: string, value?:string,  disabled?: boolean }[],
    onSelect: Function,
    selectedOption: { label: string, value?: string, disabled?:boolean }
}) => {
  const dimensionRef = useRef<any>();
  const [isOpen, setIsOpen] = useState(false);
  const [onUnselect, setOnUnselect] = useState(false);
  const [results, setResults] = useState(options);
  const [selectedOptions, setSelectedOptions] = useState<any[]>([]);
  const [inputValue, setInputValue] = useState<any>('');
  const [multiInputValues, setMultiInputValues] = useState([]);
  const [selectedValues, setSelectedValues] = useState<any[]>([]);
  const {t} = useTranslation();

  const handleClose = () => {
    setIsOpen(false);
  };
  const focusInput = () => {
    dimensionRef.current.focus();
  };
  const handleSelect = ({ label, value }:{ label: string, value?: string }) => {
    const newSelectedOptions = [...selectedOptions, label];
    const newSelectedValues = [...selectedValues, value];
    if (label && !selectedOptions.includes(label)) {
      setMultiInputValues([...multiInputValues, label as never]);
      setSelectedOptions([...newSelectedOptions]);
      setSelectedValues(newSelectedValues);
      setInputValue('');
      handleClose();
      focusInput();
      onSelect(newSelectedOptions, newSelectedValues);
    }
    const notSelectedOptions = options.filter((opt) => !newSelectedOptions.includes(opt.label));
    setResults(notSelectedOptions);
  };
  const findOptions = (event: any) => {
    const inputText = event.target.value;
    setInputValue(inputText);
    setIsOpen(true);
    const textLength = inputText.length;
    const filteredResults = options.filter(
      (opt) => inputText.toLowerCase() === opt.label.substring(0, textLength).toLowerCase()
    );
    const notSelectedResults = filteredResults.filter((opt) => !selectedOptions.includes(opt.label));
    if (notSelectedResults.length > 0) {
      setResults(notSelectedResults);
    } else {
      setResults([{ label: 'No Results', disabled: true }]);
    }
    if (textLength === 0 && multiInputValues.length > 0) {
      setResults(options);
      setIsOpen(false);
    }
  };

  useEffect(() => {
    setResults(options);
    if(selectedOption) {
       handleSelect({label: selectedOption.label, value: selectedOption.value as string});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, selectedOption]);

  const handleClear = (e: any) => {
    e.preventDefault();
    setMultiInputValues([]);
    setSelectedOptions([]);
    setSelectedValues([]);
    setInputValue('');
    setResults(options);
    setIsOpen(false);
    focusInput();
    onSelect([], []);
  };
  const handleMenu = () => {
    setIsOpen((isOpen) => !isOpen);
    setOnUnselect((onUnselect) => !onUnselect);
  };
  const removeValue = (i: any) => {
    const newValues = [...multiInputValues];
    const selectedNewValues = [...selectedValues ];
    newValues.splice(i, 1);
    selectedNewValues.splice(i, 1);
    const notSelectedValues = options.filter((opt: any) => !newValues.includes(opt.label as never));
    setMultiInputValues(newValues);
    setSelectedOptions(newValues);
    setResults(notSelectedValues);
    handleClose();
    setSelectedValues(selectedNewValues);
    onSelect(newValues, selectedNewValues);
  };
  return (
    <>
      <div style={{ marginRight: '10px', width: '420px' }} id={"autocomplete-menu-dic-" + label}>
        <MultiInput
          dataTestId={"autocomplete-menu-" + label}
          errorMessage="some error message"
          onClear={handleClear}
          helperText={t('pulse.selecttoapply')}
          id={"multi-autocomplete-menu-" + label}
          isAuto={true}
          isDisabled={false}
          isInvalid={false}
          isOpen={isOpen}
          isReadOnly={false}
          isRequired={false}
          label={label}
          labelRef={dimensionRef}
          multiInputValues={multiInputValues}
          onChange={findOptions}
          onClick={handleMenu}
          placeholder={placeholder}
          removeInputValue={removeValue}
          showClearIcon={multiInputValues.length > 0}
          size={INPUT_SIZES.STANDARD}
          value={inputValue}
          ref={dimensionRef}
        />
      </div>
      <Autocomplete
        dataTestId={"autocomplete-menu" + label}
        dimensionRef={dimensionRef}
        labelRef={dimensionRef}
        style={{ width: '224px' }}
        onClose={handleClose}
        onChange={findOptions}
        onUnselect={onUnselect}
        isOpen={isOpen}
        placement="bottom-start"
        isAuto={false}
      >
        {results.map((option, index) => (
          <MenuListItem
            key={`multi-autocomplete-menu-${JSON.stringify(option)}`}
            dataTestId={`autocomplete-menu-list-item-${index}`}
            isSelected={selectedOptions.includes(option.label)}
            onClick={() => handleSelect(option)}
            isDisabled={option.disabled}
          >
            {option.label}
          </MenuListItem>
        ))}
      </Autocomplete>
    </>
  );
};

export { MultiAutocomplete };